import { Box, List, Typography, Grid } from '@mui/material';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileItem } from '@/common';
import { downloadFileFromResponse, getMessageFromError } from '@/core';
import { downloadFileFromAuditorVisit } from '../api/downloadFileFromAuditorVisit';
import { useAuditorVisitDetailContext } from '../contexts/AuditorVisitDetailContext';
import { downloadFileFromDws } from '../api/downloadFileFromDs';
import { checkIfErrorIsAccessAuthorizationError } from '@/core/utils/getMessageFromError';

const ReturnedDocsFiles = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { auditorVisit, invalidateAuditorVisit } = useAuditorVisitDetailContext();

  const downloadFileMutation = useMutation({
    mutationFn: downloadFileFromAuditorVisit,
    onSuccess: async (response) => {
      await downloadFileFromResponse(response);
      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      checkIfErrorIsAccessAuthorizationError(error)
        ? enqueueSnackbar(
            t('No access authorisation!', { defaultValue: 'No access authorisation!' }),
            { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } }
          )
        : enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  const downloadFileFromDsMutation = useMutation({
    mutationFn: downloadFileFromDws,
    onSuccess: async (result) => {
      const res = result.res;
      const blob = await res.blob();
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', result.filename);

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      checkIfErrorIsAccessAuthorizationError(error)
        ? enqueueSnackbar(
            t('No access authorisation!', { defaultValue: 'No access authorisation!' }),
            { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } }
          )
        : enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });
  return (
    <Grid container spacing={3}>
      <Grid item xs={6}>
        <Box>
          {auditorVisit.reports && auditorVisit.reports.some((item) => item.files.length > 0) ? (
            <List>
              {auditorVisit.reports.map((item) => {
                const retArr: JSX.Element[] = [];
                if (item.files) {
                  item.files.forEach((file) =>
                    retArr.push(
                      <FileItem
                        key={file.id}
                        name={file.title || ''}
                        uploadedBy={file.uploadedBy}
                        uploadedAt={file.uploadedAt}
                        storeType={file.storeType}
                        fileName={file.fileName}
                        onDownload={() => {
                          if (file.storeType === 'DocumentSystem' && file.dwsDok) {
                            downloadFileFromDsMutation.mutate({
                              dok: file.dwsDok,
                              filename: file.title || file.dwsDok.originalfilename,
                            });
                          } else {
                            downloadFileMutation.mutate({ id: auditorVisit.id, fileId: file.id });
                          }
                        }}
                      />
                    )
                  );
                }
                return retArr;
              })}
            </List>
          ) : (
            <Typography variant="subtitle1">
              {t('No files', { defaultValue: 'No files' })}
            </Typography>
          )}
        </Box>
      </Grid>
      <Grid item xs={6}>
        <Box>
          {auditorVisit.checkList &&
          auditorVisit.checkList.some((item) => item.files.length > 0) ? (
            <List>
              {auditorVisit.checkList.map((item) => {
                const retArr: JSX.Element[] = [];
                if (item.files) {
                  item.files.forEach((file) =>
                    retArr.push(
                      <FileItem
                        key={file.id}
                        name={file.title || ''}
                        uploadedBy={file.uploadedBy}
                        uploadedAt={file.uploadedAt}
                        storeType={file.storeType}
                        fileName={file.fileName}
                        onDownload={() => {
                          if (file.storeType === 'DocumentSystem' && file.dwsDok) {
                            downloadFileFromDsMutation.mutate({
                              dok: file.dwsDok,
                              filename: file.title || file.dwsDok.originalfilename,
                            });
                          } else {
                            downloadFileMutation.mutate({ id: auditorVisit.id, fileId: file.id });
                          }
                        }}
                      />
                    )
                  );
                }
                return retArr;
              })}
            </List>
          ) : (
            <Typography variant="subtitle1">
              {t('No files', { defaultValue: 'No files' })}
            </Typography>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default ReturnedDocsFiles;
